import React, { useCallback, useEffect, useRef, useState } from "react";
import { Search, Pencil, Trash2, Plus, X, ImagePlus, AlertTriangle } from "lucide-react";

import Topbar from "../components/Topbar";
import { supabase } from "../lib/supabase";
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  TableSkeleton,
  useToast,
} from "../components/ui/Feedback";
import { FilterTabs, Pagination, useDebounced } from "../components/ui/Table";
import { useQuery } from "../lib/useQuery";
import {
  ProductFormFields,
  fieldIsSaveable,
  fieldPatch,
  ProductFormValues,
  buildProductPayload,
  emptyProductForm,
  toFormValues,
  validateProduct,
} from "../components/ProductForm";
import { friendlyError } from "../lib/errorMessage";

const BUCKET = "product-images";

// Extracts the storage path from a public Supabase URL, e.g.
// https://xxx.supabase.co/storage/v1/object/public/product-images/abc.jpg -> abc.jpg
function getStoragePathFromUrl(url: string): string | null {
  const marker = `/object/public/${BUCKET}/`;
  const idx = url.indexOf(marker);
  if (idx === -1) return null;
  return url.substring(idx + marker.length);
}

async function deleteImagesFromStorage(urls: string[]) {
  const paths = urls.map(getStoragePathFromUrl).filter((p): p is string => !!p);
  if (!paths.length) return;
  const { error } = await supabase.storage.from(BUCKET).remove(paths);
  if (error) console.error("Failed to delete storage images:", error.message);
}

async function uploadProductImages(files: File[]): Promise<string[]> {
  const urls: string[] = [];

  for (const file of files) {
    const ext = file.name.split(".").pop();
    const fileName = `${crypto.randomUUID()}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from(BUCKET)
      .upload(fileName, file, { cacheControl: "3600", upsert: false });

    if (uploadError) {
      throw new Error(`Failed to upload ${file.name}: ${uploadError.message}`);
    }

    const { data } = supabase.storage.from(BUCKET).getPublicUrl(fileName);
    urls.push(data.publicUrl);
  }

  return urls;
}

function ImageDropzone({
  existingImages = [],
  newPreviews,
  onSelectFiles,
  onRemoveExisting,
  onRemoveNew,
}: {
  existingImages?: string[];
  newPreviews: string[];
  onSelectFiles: (files: File[]) => void;
  onRemoveExisting?: (index: number) => void;
  onRemoveNew: (index: number) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files ?? []).filter((f) =>
      f.type.startsWith("image/")
    );
    if (files.length) onSelectFiles(files);
  }

  function handlePick(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (files.length) onSelectFiles(files);
    e.target.value = "";
  }

  const hasImages = existingImages.length > 0 || newPreviews.length > 0;

  return (
    <div>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        style={{
          border: `1.5px dashed ${
            dragOver ? "var(--primary)" : "var(--border, #d9dce3)"
          }`,
          borderRadius: 12,
          padding: "22px 16px",
          textAlign: "center",
          cursor: "pointer",
          background: dragOver ? "var(--primary-light)" : "var(--bg)",
          transition: "all 0.15s ease",
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: "none" }}
          onChange={handlePick}
        />
        <ImagePlus
          size={22}
          color="var(--primary)"
          style={{ marginBottom: 6 }}
        />
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "var(--text-primary)",
          }}
        >
          Click to upload or drag and drop
        </div>
        <div
          style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 2 }}
        >
          PNG, JPG up to a few MB each — multiple files allowed
        </div>
      </div>

      {hasImages && (
        <div
          style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}
        >
          {existingImages.map((src, i) => (
            <div
              key={`existing-${i}`}
              className="thumb-wrap"
              style={{
                position: "relative",
                width: 64,
                height: 64,
                borderRadius: 10,
                overflow: "hidden",
                boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
              }}
            >
              <img
                src={src}
                alt=""
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
              {onRemoveExisting && (
                <button
                  type="button"
                  onClick={() => onRemoveExisting(i)}
                  className="thumb-remove"
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(0,0,0,0.45)",
                    border: "none",
                    color: "#fff",
                    cursor: "pointer",
                    opacity: 0,
                    transition: "opacity 0.15s ease",
                  }}
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          ))}
          {newPreviews.map((src, i) => (
            <div
              key={`new-${i}`}
              className="thumb-wrap"
              style={{
                position: "relative",
                width: 64,
                height: 64,
                borderRadius: 10,
                overflow: "hidden",
                boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                outline: "2px solid var(--primary)",
                outlineOffset: 1,
              }}
            >
              <img
                src={src}
                alt=""
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
              <button
                type="button"
                onClick={() => onRemoveNew(i)}
                className="thumb-remove"
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(0,0,0,0.45)",
                  border: "none",
                  color: "#fff",
                  cursor: "pointer",
                  opacity: 0,
                  transition: "opacity 0.15s ease",
                }}
              >
                <Trash2 size={16} />
              </button>
              <div
                style={{
                  position: "absolute",
                  bottom: 2,
                  left: 2,
                  fontSize: 9,
                  fontWeight: 700,
                  color: "#fff",
                  background: "var(--primary)",
                  padding: "1px 5px",
                  borderRadius: 5,
                }}
              >
                NEW
              </div>
            </div>
          ))}
        </div>
      )}

      <style>{`
        .thumb-wrap:hover .thumb-remove { opacity: 1; }
      `}</style>
    </div>
  );
}

const PAGE_SIZE = 30;

const money = (amount: number | null, currency: "GBP" | "USD") =>
  amount == null
    ? null
    : new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(amount);

/**
 * Products.
 *
 * Rebuilt around a single form. There used to be two — an Add form with seven
 * fields and an Edit form with four — against a table of seventeen columns, so
 * `stock_count`, `categories`, `sub_category`, `how_to_use`, `in_stock` and
 * both native price columns could not be set anywhere in the panel.
 *
 * The pricing gap was the expensive one. The app resolves price from
 * `price_gbp` / `price_usd` and treats a missing one as "not sold in that
 * market" rather than converting. Because the old form only wrote the legacy
 * `price` + `currency` pair, every product created here was unbuyable in the
 * UK, and in the US fell back to `price` — charging $24.99 for a £24.99
 * product. Both native prices are now required fields in all but name, and the
 * table below shows each one so a missing price is visible at a glance rather
 * than discovered by a customer.
 */
export default function Products() {
  const toast = useToast();

  const [search, setSearch] = useState("");
  // Item 12: the catalogue is mostly unlisted right now, and there was no way
  // to see which products those were without scrolling the whole table.
  const [listing, setListing] = useState<"all" | "listed" | "unlisted">("all");
  const [offset, setOffset] = useState(0);
  const debounced = useDebounced(search);

  const list = useQuery(async () => {
    let query = supabase
      .from("products")
      .select("*", { count: "exact" })
      .order("created_at", { ascending: false })
      .range(offset, offset + PAGE_SIZE - 1);

    if (listing === "listed") query = query.eq("in_stock", true);
    if (listing === "unlisted") query = query.eq("in_stock", false);

    const term = debounced.trim().replace(/[%,(){}"'\\]/g, " ").trim();
    if (term) query = query.or(`name.ilike.%${term}%,brand.ilike.%${term}%`);

    const { data, error, count } = await query;
    if (error) throw new Error(error.message);
    return { rows: data ?? [], total: count ?? 0 };
  }, [debounced, offset, listing]);

  const rows = list.data?.rows ?? [];

  // One modal for both paths. `editing` null means "adding".
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [values, setValues] = useState<ProductFormValues>(emptyProductForm);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [newPreviews, setNewPreviews] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formError, setFormError] = useState("");
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<any | null>(null);
  const [deletingBusy, setDeletingBusy] = useState(false);

  // ── Auto-save bookkeeping ───────────────────────────────────────────────
  //
  // `autoSaveState` drives the header indicator. `held` names the fields that
  // were deliberately NOT written because the value would be invalid — the
  // one case where silence would be a lie, since the admin has no Save button
  // to press and no error to read.
  const [autoSaveState, setAutoSaveState] = useState<"idle" | "saving" | "error">("idle");
  const [held, setHeld] = useState<string[]>([]);

  // The blurred field is written from a ref, not from the `values` captured in
  // the callback's closure. A blur can arrive in the same commit as the
  // keystroke that produced it, and the closure would then be one character
  // behind — writing the value as it was before the last edit.
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const imagesRef = useRef(existingImages);
  imagesRef.current = existingImages;

  // What is actually in the database for the row being edited, so an
  // untouched field that merely lost focus doesn't cause a pointless write
  // (and a misleading "Saved").
  const persistedRef = useRef<Record<string, string>>({});

  // Set when any field has been auto-saved this session. The table behind the
  // modal is a snapshot from before those writes, so closing without a
  // refetch would show the old price back — which reads exactly like the
  // auto-save never happened.
  const dirtyRef = useRef(false);

  // ── Bulk selection ──────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  // Selection is cleared whenever the visible set changes. Keeping it across
  // a search or a page turn means the confirm dialog would be counting rows
  // that are no longer on screen — the classic way to delete something you
  // never actually looked at.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [debounced, offset, listing]);

  const pageIds = rows.map((r: any) => r.id as string);
  const selectedOnPage = pageIds.filter((id) => selectedIds.has(id));
  const allOnPageSelected = pageIds.length > 0 && selectedOnPage.length === pageIds.length;

  const toggleOne = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAllOnPage = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });

  // Cleared per modal session so "Changes saved" always refers to this edit.
  useEffect(() => {
    setSavedAt(null);
  }, [editing?.id, modalOpen]);

  const openAdd = useCallback(() => {
    setEditing(null);
    setValues(emptyProductForm);
    setExistingImages([]);
    setNewFiles([]);
    setNewPreviews([]);
    setFormError("");
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((product: any) => {
    const initial = toFormValues(product);
    setEditing(product);
    setValues(initial);
    setExistingImages(product.images ?? []);
    setNewFiles([]);
    setNewPreviews([]);
    setFormError("");
    setAutoSaveState("idle");
    setHeld([]);
    dirtyRef.current = false;
    // Serialised so arrays and booleans compare as reliably as strings.
    persistedRef.current = Object.fromEntries(
      Object.entries(initial).map(([k, v]) => [k, JSON.stringify(v)])
    );
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    // Object URLs leak until revoked; the previous version revoked them in
    // some exit paths and not others.
    setNewPreviews((prev) => {
      prev.forEach((url) => URL.revokeObjectURL(url));
      return [];
    });
    setModalOpen(false);
    setEditing(null);
    setNewFiles([]);
    setFormError("");
    // The table was loaded before the auto-saves happened. Without this it
    // redraws with the pre-edit values, which is indistinguishable from the
    // auto-save having done nothing.
    if (dirtyRef.current) {
      dirtyRef.current = false;
      list.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function selectFiles(files: File[]) {
    setNewFiles((prev) => [...prev, ...files]);
    setNewPreviews((prev) => [...prev, ...files.map((f) => URL.createObjectURL(f))]);
  }

  function removeExisting(index: number) {
    setExistingImages((prev) => prev.filter((_, i) => i !== index));
  }

  function removeNew(index: number) {
    setNewFiles((prev) => prev.filter((_, i) => i !== index));
    setNewPreviews((prev) => {
      URL.revokeObjectURL(prev[index]);
      return prev.filter((_, i) => i !== index);
    });
  }

  async function save() {
    const problem = validateProduct(values);
    if (problem) {
      setFormError(problem);
      return;
    }

    setFormError("");
    setSaving(true);

    let uploadedUrls: string[] = [];
    if (newFiles.length) {
      setUploading(true);
      try {
        uploadedUrls = await uploadProductImages(newFiles);
      } catch (err: any) {
        setFormError(err.message ?? "Image upload failed");
        setSaving(false);
        setUploading(false);
        return;
      }
      setUploading(false);
    }

    const images = [...existingImages, ...uploadedUrls];
    const payload = buildProductPayload(values, images);

    const { error } = editing
      ? await supabase.from("products").update(payload).eq("id", editing.id)
      : await supabase.from("products").insert(payload);

    setSaving(false);

    if (error) {
      // The uploaded images are deliberately left in place: the user can press
      // Save again without re-picking them, and an orphan is cheaper than
      // making someone redo the upload.
      setFormError(error.message);
      return;
    }

    // Only once the write succeeded — the old code deleted removed images
    // before knowing whether the update landed.
    if (editing) {
      const removed = (editing.images ?? []).filter(
        (url: string) => !existingImages.includes(url)
      );
      if (removed.length) await deleteImagesFromStorage(removed);
    }

    toast.success(editing ? "Product updated" : "Product added");
    closeModal();
    list.refetch();
  }

  /**
   * Per-field auto-save (list item 18).
   *
   * Only ever runs when editing an existing product — there is no row to
   * patch while adding, so that path keeps the Save button. Writes just the
   * blurred field, and skips the write entirely when the value would fail
   * validation, since a silent save has no way to report a rejection.
   */
  const autoSaveField = useCallback(
    async (field: keyof ProductFormValues) => {
      if (!editing) return;

      // Read through the refs, not the closure — see valuesRef above.
      const current = valuesRef.current;
      const images = imagesRef.current;

      // Untouched field that merely lost focus. Nothing to write, and
      // flashing "Saved" for it would train the admin to ignore the word.
      const serialised = JSON.stringify(current[field]);
      if (persistedRef.current[field] === serialised) return;

      if (!fieldIsSaveable(field, current)) {
        setHeld((prev) => (prev.includes(field) ? prev : [...prev, field]));
        return;
      }
      setHeld((prev) => prev.filter((f) => f !== field));

      const patch = fieldPatch(field, current, images);
      if (!patch) return;

      setAutoSaveState("saving");
      const { error } = await supabase.from("products").update(patch).eq("id", editing.id);

      if (error) {
        // Surfaced rather than swallowed: without the Save button there is
        // no other moment the admin would learn the write failed.
        setAutoSaveState("error");
        toast.failure(friendlyError(error, "Something went wrong. Please try again."));
        return;
      }

      persistedRef.current[field] = serialised;
      // `categories` carries `ingredients` with it, and `price_usd` carries
      // the legacy price columns. Recording only the blurred field would make
      // the passenger look unsaved and write it again on the next blur.
      if (field === "categories") {
        persistedRef.current.ingredients = JSON.stringify(current.ingredients);
      }
      dirtyRef.current = true;
      setAutoSaveState("idle");
      setSavedAt(Date.now());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editing, toast]
  );

  async function confirmDelete() {
    if (!deleting) return;
    setDeletingBusy(true);
    const { error } = await supabase.from("products").delete().eq("id", deleting.id);
    setDeletingBusy(false);

    if (error) {
      // Returning here matters beyond the message: the storage cleanup used to
      // run even when the row survived, stripping images off a live product.
      toast.failure(friendlyError(error, "Something went wrong. Please try again."));
      return;
    }

    if (deleting.images?.length) await deleteImagesFromStorage(deleting.images);
    toast.success("Product deleted");
    setDeleting(null);
    list.refetch();
  }

  /**
   * Bulk delete.
   *
   * `.select()` on the delete is the important part: it returns the rows the
   * database actually removed. A row can survive a delete with no error at
   * all — RLS filters it out silently, and a product referenced by an
   * existing order may be held back by a foreign key. Counting what came
   * back is the only honest way to report the result, and it's what decides
   * which images are safe to purge: cleaning storage off the requested ids
   * would strip images from products that are still live.
   */
  async function confirmBulkDelete() {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    setBulkBusy(true);
    const { data: deletedRows, error } = await supabase
      .from("products")
      .delete()
      .in("id", ids)
      .select("id, images");
    setBulkBusy(false);

    if (error) {
      toast.failure(friendlyError(error, "Something went wrong. Please try again."));
      return;
    }

    const deleted = deletedRows ?? [];
    const images = deleted.flatMap((r: any) => r.images ?? []);
    if (images.length) await deleteImagesFromStorage(images);

    const missed = ids.length - deleted.length;
    if (missed > 0) {
      toast.failure(
        `Deleted ${deleted.length} of ${ids.length}. ${missed} could not be removed — they may be referenced by existing orders.`
      );
    } else {
      toast.success(`${deleted.length} product${deleted.length === 1 ? "" : "s"} deleted`);
    }

    setSelectedIds(new Set());
    setBulkOpen(false);
    list.refetch();
  }

  return (
    <div>
      <Topbar
        title="Products"
        subtitle={list.data ? `${list.data.total.toLocaleString()} in the catalogue` : undefined}
        actions={
          <button className="btn btn-primary" onClick={openAdd}>
            <Plus size={15} /> Add product
          </button>
        }
      />

      <div className="page-shell">
        <div className="card">
          <div style={{ position: "relative", maxWidth: 380, marginBottom: 16 }}>
            <Search
              size={14}
              color="var(--text-muted)"
              style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }}
            />
            <input
              className="input"
              placeholder="Search name or brand…"
              style={{ paddingLeft: 34 }}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setOffset(0);
              }}
            />
          </div>

          {/* Item 12 — "not currently listed" means in_stock = false, the
              flag the storefront filters on. Stock count is separate: a
              listed product can still be sold out. */}
          <div style={{ marginTop: 12 }}>
            <FilterTabs
              tabs={[
                { key: "all", label: "All" },
                { key: "listed", label: "Listed in store" },
                { key: "unlisted", label: "Not listed", tone: "var(--danger, #e5484d)" },
              ]}
              active={listing}
              onChange={(key) => {
                setListing(key);
                setOffset(0);
              }}
            />
          </div>

          {list.error ? (
            <ErrorState error={list.error} onRetry={list.refetch} />
          ) : (
            <>
              {selectedIds.size > 0 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    flexWrap: "wrap",
                    padding: "11px 14px",
                    marginBottom: 12,
                    borderRadius: 10,
                    background: "var(--primary-light)",
                    border: "1px solid rgba(108, 95, 252, 0.18)",
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>
                    {selectedIds.size} selected
                    {/* Selection survives nothing but the current view, yet it
                        can still exceed what's on screen if the page is only
                        partly ticked — say so rather than implying otherwise. */}
                    {selectedOnPage.length !== selectedIds.size &&
                      ` (${selectedOnPage.length} on this page)`}
                  </span>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className="btn" onClick={() => setSelectedIds(new Set())}>
                      Clear
                    </button>
                    <button
                      className="btn"
                      onClick={() => setBulkOpen(true)}
                      style={{
                        background: "var(--danger, #e5484d)",
                        borderColor: "transparent",
                        color: "#fff",
                      }}
                    >
                      <Trash2 size={14} /> Delete selected
                    </button>
                  </div>
                </div>
              )}

              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}>
                        <input
                          type="checkbox"
                          checked={allOnPageSelected}
                          // Indicates "some but not all", so the header box
                          // never claims a full-page selection it doesn't have.
                          ref={(el) => {
                            if (el) {
                              el.indeterminate =
                                selectedOnPage.length > 0 && !allOnPageSelected;
                            }
                          }}
                          onChange={toggleAllOnPage}
                          disabled={pageIds.length === 0}
                          aria-label="Select all products on this page"
                          style={{ cursor: "pointer" }}
                        />
                      </th>
                      <th>Product</th>
                      <th>GBP</th>
                      <th>USD</th>
                      <th>Stock</th>
                      <th>Status</th>
                      <th style={{ textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.loading && <TableSkeleton rows={8} cols={7} />}

                    {!list.loading &&
                      rows.map((product: any) => {
                        const gbp = money(product.price_gbp, "GBP");
                        const usd = money(product.price_usd, "USD");
                        const low = product.in_stock && (product.stock_count ?? 0) <= 5;

                        return (
                          <tr
                            key={product.id}
                            onClick={() => openEdit(product)}
                            style={{ cursor: "pointer" }}
                          >
                            {/* stopPropagation on the cell as well as the
                                input: the whole row opens the edit modal, so
                                without it ticking a box would also open the
                                product you were only trying to select. */}
                            <td onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={selectedIds.has(product.id)}
                                onChange={() => toggleOne(product.id)}
                                aria-label={`Select ${product.name}`}
                                style={{ cursor: "pointer" }}
                              />
                            </td>
                            <td>
                              <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                                {product.images?.[0] ? (
                                  <img
                                    src={product.images[0]}
                                    alt=""
                                    style={{
                                      width: 40,
                                      height: 40,
                                      borderRadius: 9,
                                      objectFit: "cover",
                                      border: "1px solid var(--border)",
                                    }}
                                  />
                                ) : (
                                  <div
                                    style={{
                                      width: 40,
                                      height: 40,
                                      borderRadius: 9,
                                      background: "var(--bg)",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <ImagePlus size={15} color="var(--text-light)" />
                                  </div>
                                )}
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontWeight: 700, color: "var(--text-primary)" }}>
                                    {product.name}
                                  </div>
                                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                    {product.brand}
                                    {product.sub_category ? ` · ${product.sub_category}` : ""}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* Both native prices get a column. A missing one
                                means the product cannot be sold in that market
                                at all, which is worth seeing in the list rather
                                than discovering from a customer. */}
                            <td style={{ fontWeight: 700 }}>
                              {gbp ?? <MissingPrice market="UK" />}
                            </td>
                            <td style={{ fontWeight: 700 }}>
                              {usd ?? <MissingPrice market="US" />}
                            </td>

                            <td>
                              <span style={{ fontWeight: 700, color: low ? "#b97800" : undefined }}>
                                {product.stock_count ?? 0}
                              </span>
                              {low && (
                                <div style={{ fontSize: 11, color: "#b97800", fontWeight: 700 }}>
                                  low
                                </div>
                              )}
                            </td>

                            <td>
                              {product.in_stock ? (
                                <span className="badge badge-success">Listed</span>
                              ) : (
                                <span className="badge">Hidden</span>
                              )}
                            </td>

                            <td onClick={(e) => e.stopPropagation()} style={{ textAlign: "right" }}>
                              <div style={{ display: "inline-flex", gap: 6 }}>
                                <button
                                  className="btn btn-sm"
                                  title="Edit"
                                  onClick={() => openEdit(product)}
                                  style={{
                                    background: "var(--primary-light)",
                                    color: "var(--primary)",
                                    border: "none",
                                  }}
                                >
                                  <Pencil size={13} />
                                </button>
                                <button
                                  className="btn btn-sm btn-danger"
                                  title="Delete"
                                  onClick={() => setDeleting(product)}
                                  style={{ border: "none" }}
                                >
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}

                    {!list.loading && rows.length === 0 && (
                      <tr>
                        <td colSpan={7} style={{ padding: 0 }}>
                          <EmptyState
                            message="No products yet"
                            hint={search ? `Nothing matching “${search}”` : "Add your first product to the catalogue."}
                          />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <Pagination
                offset={offset}
                limit={PAGE_SIZE}
                total={list.data?.total ?? 0}
                onChange={setOffset}
                busy={list.fetching}
              />
            </>
          )}
        </div>
      </div>

      {modalOpen && (
        <div
          onClick={closeModal}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(24, 27, 46, 0.42)",
            backdropFilter: "blur(3px)",
            zIndex: 400,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            padding: "40px 20px",
            overflowY: "auto",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: "var(--radius-lg)",
              boxShadow: "var(--shadow-md)",
              width: "100%",
              maxWidth: 720,
            }}
          >
            <header
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "20px 24px",
                borderBottom: "1px solid var(--border)",
              }}
            >
              <div>
                <div style={{ fontSize: 17, fontWeight: 850, letterSpacing: "-0.03em" }}>
                  {editing ? "Edit product" : "Add product"}
                </div>
                {editing ? (
                  <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                    {editing.name}
                    {/* Auto-save is silent by design, but "silent" shouldn't
                        mean "no idea whether it worked" — this is the only
                        signal that a field was written. */}
                    {autoSaveState === "saving" ? (
                      <span style={{ fontWeight: 700, marginLeft: 8 }}>· Saving…</span>
                    ) : autoSaveState === "error" ? (
                      <span style={{ color: "var(--danger)", fontWeight: 700, marginLeft: 8 }}>
                        · Couldn’t save — check the message and try again
                      </span>
                    ) : savedAt ? (
                      <span style={{ color: "var(--success, #2e7d5b)", fontWeight: 700, marginLeft: 8 }}>
                        · Saved
                      </span>
                    ) : (
                      <span style={{ marginLeft: 8 }}>· Each field saves as you leave it</span>
                    )}
                  </div>
                ) : (
                  // Auto-save needs a row to patch, and there isn't one yet.
                  // Saying so beats letting the admin discover it by losing
                  // work.
                  <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                    Press “Add product” to create it — auto-save starts after that.
                  </div>
                )}
              </div>
              <button className="btn btn-sm" onClick={closeModal} aria-label="Close">
                <X size={14} />
              </button>
            </header>

            <div style={{ padding: 24 }}>
              <ProductFormFields
                values={values}
                onChange={setValues}
                onCommit={autoSaveField}
              />

              <div style={{ marginTop: 4, marginBottom: 16 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: "var(--text-muted)",
                    marginBottom: 8,
                  }}
                >
                  Images
                </label>
                <ImageDropzone
                  existingImages={existingImages}
                  newPreviews={newPreviews}
                  onSelectFiles={selectFiles}
                  onRemoveExisting={removeExisting}
                  onRemoveNew={removeNew}
                />
              </div>

              {/* A field held back by validation writes nothing and shows no
                  error of its own, so without this the admin would leave
                  believing an invalid price had been stored. */}
              {editing && held.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    gap: 9,
                    alignItems: "flex-start",
                    background: "var(--warning-light, #fff6e5)",
                    border: "1px solid #f0d9a8",
                    borderRadius: "var(--radius)",
                    padding: "11px 13px",
                    fontSize: 13,
                    color: "#8a5a00",
                    fontWeight: 650,
                    marginBottom: 16,
                  }}
                >
                  <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>
                    Not saved yet: {held.map((f) => f.replace(/_/g, " ")).join(", ")}. Fix the
                    value and it will save when you leave the field.
                  </span>
                </div>
              )}

              {formError && (
                <div
                  style={{
                    display: "flex",
                    gap: 9,
                    alignItems: "flex-start",
                    background: "var(--danger-light)",
                    border: "1px solid #f6c9cf",
                    borderRadius: "var(--radius)",
                    padding: "11px 13px",
                    fontSize: 13,
                    color: "var(--danger)",
                    fontWeight: 650,
                    marginBottom: 16,
                  }}
                >
                  <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                  {formError}
                </div>
              )}
            </div>

            <footer
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                padding: "16px 24px",
                borderTop: "1px solid var(--border)",
              }}
            >
              <button className="btn" onClick={closeModal} disabled={saving}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {uploading
                  ? "Uploading images…"
                  : saving
                    ? "Saving…"
                    : editing
                      ? "Save changes"
                      : "Add product"}
              </button>
            </footer>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleting}
        busy={deletingBusy}
        destructive
        title="Delete this product?"
        body={
          <>
            Removes <strong>{deleting?.name}</strong> and its images permanently.
            Past orders keep their own copy of the name and price, so order
            history is unaffected.
          </>
        }
        confirmLabel="Delete"
        onConfirm={confirmDelete}
        onCancel={() => setDeleting(null)}
      />

      <ConfirmDialog
        open={bulkOpen}
        busy={bulkBusy}
        destructive
        title={`Delete ${selectedIds.size} product${selectedIds.size === 1 ? "" : "s"}?`}
        body={
          <>
            Permanently removes {selectedIds.size} product
            {selectedIds.size === 1 ? "" : "s"} and their images. Past orders
            keep their own copy of the name and price, so order history is
            unaffected. Any product still referenced by an order may be kept
            by the database — you&apos;ll be told how many were actually
            removed.
          </>
        }
        confirmLabel={`Delete ${selectedIds.size}`}
        onConfirm={confirmBulkDelete}
        onCancel={() => setBulkOpen(false)}
      />
    </div>
  );
}

/** A price that isn't set, said plainly — this is a "cannot be sold" state. */
function MissingPrice({ market }: { market: string }) {
  return (
    <span
      title={`No price set — this product cannot be bought in the ${market}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        fontSize: 11.5,
        fontWeight: 700,
        color: "var(--danger)",
      }}
    >
      <AlertTriangle size={11} /> not sold
    </span>
  );
}
